import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { Signal } from '@angular/core';

import { IImportColumn, IImportConfiguration, IImportFileExtract, IImportItem } from '../../models';
import { ImportConfigurationStatus, ImportFileColumn, ImportStep, ImportType } from '../../enums';

export function withImportDetailGetters<_>() {
	return signalStoreFeature(
		{
			state: type<{
				importItem: IImportItem,
				importConfiguration: IImportConfiguration,
			}>(),
			props: type<{ currentType: Signal<ImportType> }>(),
		},

		withMethods(store => {
			const isMapped = (col: ImportFileColumn | string): boolean => !!store.importItem().columnsMapping.find(m => m.columnName && m.mapsTo === col);
			
			const areRequiredColumnsMapped = (): boolean => {
				if (!store.importItem() || !store.importConfiguration()?.importColumns) {
					return false;
				}

				const importColumns = store.importConfiguration().importColumns.find(c => c.importType === store.importItem().type);

				if (!importColumns) return false;

				const requiredColumns =  importColumns.importColumns.filter(ic => ic.isRequired).map(ic => ic.importColumn);

				return requiredColumns.length && !requiredColumns.find(col => !isMapped(col));
			};
			
			const getFileExtract = (): IImportFileExtract => {
				if (!store.importItem()) return {lines: [], headers: []};
				return store.importItem().fileExtract;
			};
			
			const getConfigWorkerMessage = (): string => {
				if (!store.importItem() || !store.importItem().importFileConfiguration) {
					return null;
				}
				return store.importItem().importFileConfiguration.extraMessage;
			};

			const findFirstAccountingImportError = (): string => {
				if (store.importItem().documentTypesMapping.length === 0 || store.importItem().paymentTypesMapping.length === 0) {
					return 'Import.Creation.ValuesMapping.EmptyAccountingDocumentOrPaymentMeanTypes';
				}
				return null;
			}
			
			const getValuesMappingInError = (): string | { label: string, toTranslate: boolean } => {
				// if required columns are not mapped, there is no need to go further
				// remove me if we want to display mapping errors still
				if (!areRequiredColumnsMapped()) return null;

				const importItem = store.importItem();

				if (!importItem || !importItem.importFileConfiguration) return null;

				const importFileConfig = importItem.importFileConfiguration;
				const importFileConfigStatus = importFileConfig.importConfigurationStatus;
				const currentStep = importFileConfig.currentStep;
				const isOnStepMappingValueOrHigher = currentStep === ImportStep.MappingValue;

				if (importFileConfigStatus === ImportConfigurationStatus.Error && isOnStepMappingValueOrHigher) {
					return importFileConfig.extraMessage;
				}

				const onColumnPositionStep = currentStep === ImportStep.ColumnPosition;

				if (!onColumnPositionStep && importFileConfigStatus === ImportConfigurationStatus.Ready) {
					let label;

					switch (importItem.type) {
					case ImportType.Cashing:
					case ImportType.Accounting:
						label = findFirstAccountingImportError();
						break;
					case ImportType.Debtor:
					case ImportType.Contact:
						label = null
						break;
					}

					if (label) {
						return { toTranslate: true, label };
					}
				}

				return null;
			};
			
			const getImportColumnsByType = (): IImportColumn[] => {
				const allColumns = store.importConfiguration()?.importColumns;
				const columnsByType = allColumns.find(c => c.importType === store.currentType());
				return columnsByType ? columnsByType.importColumns : [];
			};

			return {
				areRequiredColumnsMapped,
				getFileExtract,
				getConfigWorkerMessage,
				getValuesMappingInError,
				getImportColumnsByType,
			}
		})
	)
}